<template>
    <div>
        <!-- Overlay Backdrop -->
        <div
            class="full-search-results-backdrop"
            :class="{ show: show }"
            @click="close"
        ></div>

        <!-- Full Search Results Sidebar -->
        <div
            class="full-search-results-sidebar"
            :class="{ 'sidebar-collapsed': !show }"
        >
            <!-- Sidebar Header -->
            <div class="sidebar-header">
                <h3 class="sidebar-title">Search Results for "{{ searchQuery }}"</h3>
                <button
                    @click="close"
                    class="sidebar-toggle-btn"
                    title="Close sidebar"
                >
                    <FontAwesomeIcon :icon="faXmark" />
                </button>
            </div>

            <!-- Search Results Tabs -->
            <div class="search-results-tabs">
                <el-tabs v-model="activeTab" @tab-change="handleTabChange">
                    <!-- Projects Tab -->
                    <el-tab-pane label="Projects" name="project">
                        <div class="results-container">
                            <div v-if="isLoading" class="loading-results">
                                <font-awesome-icon
                                    :icon="['fas', 'spinner']"
                                    spin
                                />
                                <p class="mt-3">Loading projects...</p>
                            </div>
                            <div
                                v-else-if="!searchResults.projects?.length"
                                class="empty-results"
                            >
                                <font-awesome-icon :icon="['fas', 'folder']" />
                                <span>No projects found</span>
                            </div>
                            <div v-else class="results-list">
                                <div
                                    v-for="project in searchResults.projects"
                                    :key="project.id"
                                    class="result-item"
                                    @click="navigateToProject(project)"
                                >
                                    <div class="result-item-content">
                                        <div class="result-item-left">
                                            <i
                                                class="fas fa-folder result-item-icon"
                                            ></i>
                                            <div class="result-item-details">
                                                <div class="result-item-title">
                                                    {{ project.title || project.name }}
                                                </div>
                                                <div class="result-item-meta">
                                                    <span class="charge-code">
                                                        {{ project.charge_code }}
                                                    </span>
                                                    <span class="status" :class="project.status?.toLowerCase()">
                                                        {{ project.status }}
                                                    </span>
                                                </div>
                                                <div class="result-item-description" v-if="project.description">
                                                    {{ project.description }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="result-item-arrow">
                                            <font-awesome-icon
                                                :icon="['fas', 'chevron-right']"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- Documents Tab -->
                    <el-tab-pane label="Documents" name="document">
                        <div class="results-container">
                            <div v-if="isLoading" class="loading-results">
                                <font-awesome-icon
                                    :icon="['fas', 'spinner']"
                                    spin
                                />
                                <p class="mt-3">Loading documents...</p>
                            </div>
                            <div
                                v-else-if="!searchResults.documents?.length"
                                class="empty-results"
                            >
                                <font-awesome-icon :icon="['fas', 'file-alt']" />
                                <span>No documents found</span>
                            </div>
                            <div v-else class="results-list">
                                <div
                                    v-for="document in searchResults.documents"
                                    :key="document.id"
                                    class="result-item"
                                    @click="navigateToDocument(document)"
                                >
                                    <div class="result-item-content">
                                        <div class="result-item-left">
                                            <i
                                                class="fas fa-file-alt result-item-icon"
                                            ></i>
                                            <div class="result-item-details">
                                                <div class="result-item-title">
                                                    {{ document.title || document.name }}
                                                </div>
                                                <div class="result-item-meta">
                                                    <span class="category">
                                                        {{ document.category }}
                                                    </span>
                                                    <span class="views" v-if="document.view_count">
                                                        {{ document.view_count }} views
                                                    </span>
                                                </div>
                                                <div class="result-item-description" v-if="document.description">
                                                    {{ document.description }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="result-item-arrow">
                                            <font-awesome-icon
                                                :icon="['fas', 'chevron-right']"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- TAMers Tab -->
                    <el-tab-pane label="TAMers" name="tamer">
                        <div class="results-container">
                            <div v-if="isLoading" class="loading-results">
                                <font-awesome-icon
                                    :icon="['fas', 'spinner']"
                                    spin
                                />
                                <p class="mt-3">Loading TAMers...</p>
                            </div>
                            <div
                                v-else-if="!searchResults.tamers?.length"
                                class="empty-results"
                            >
                                <font-awesome-icon :icon="['fas', 'users']" />
                                <span>No TAMers found</span>
                            </div>
                            <div v-else class="results-list">
                                <div
                                    v-for="tamer in searchResults.tamers"
                                    :key="tamer.id"
                                    class="result-item"
                                    @click="navigateToTamer(tamer)"
                                >
                                    <div class="result-item-content">
                                        <div class="result-item-left">
                                            <i
                                                class="fas fa-user result-item-icon"
                                            ></i>
                                            <div class="result-item-details">
                                                <div class="result-item-title">
                                                    {{ tamer.name }}
                                                </div>
                                                <div class="result-item-meta">
                                                    <span class="email">
                                                        {{ tamer.email }}
                                                    </span>
                                                    <span class="business-unit" v-if="tamer.business_unit">
                                                        {{ tamer.business_unit }}
                                                    </span>
                                                </div>
                                                <div class="result-item-description" v-if="tamer.role">
                                                    {{ tamer.role }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="result-item-arrow">
                                            <font-awesome-icon
                                                :icon="['fas', 'chevron-right']"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useRouter } from "vue-router";
import UnifiedSearchApi from "@api/unifiedSearch.api.js";

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    searchQuery: {
        type: String,
        default: "",
    },
    initialTab: {
        type: String,
        default: "project",
    },
});

const emit = defineEmits(["close"]);

const router = useRouter();
const activeTab = ref(props.initialTab);
const isLoading = ref(false);
const searchResults = ref({
    projects: [],
    documents: [],
    tamers: [],
});

watch(
    () => props.show,
    (newVal) => {
        if (newVal && props.searchQuery) {
            fetchSearchResults();
        }
    },
);

watch(
    () => props.searchQuery,
    (newQuery) => {
        if (newQuery && props.show) {
            fetchSearchResults();
        }
    },
);

const fetchSearchResults = async () => {
    if (!props.searchQuery.trim()) return;

    isLoading.value = true;
    try {
        const response = await UnifiedSearchApi.unifiedSearch({
            keyword: props.searchQuery,
            limit: 50, // Get more results for the sidebar
        });

        if (response.data) {
            searchResults.value = {
                projects: response.data.projects || [],
                documents: response.data.documents || [],
                tamers: response.data.tamers || [],
            };
        }
    } catch (error) {
        console.error("Error fetching search results:", error);
        // Could show an error notification here
    } finally {
        isLoading.value = false;
    }
};

const handleTabChange = (tabName) => {
    activeTab.value = tabName;
};

const close = () => {
    emit("close");
};

const navigateToProject = (project) => {
    router.push({ name: "projects.view", params: { id: project.id } });
    close();
};

const navigateToDocument = (document) => {
    router.push({ name: "documents.view", params: { id: document.id } });
    close();
};

const navigateToTamer = (tamer) => {
    // Navigate to user profile or TAMer details page
    // Adjust the route name as needed
    router.push({ name: "users.profile", params: { id: tamer.id } });
    close();
};
</script>

<style scoped>
/* Backdrop */
.full-search-results-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1049;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
}

.full-search-results-backdrop.show {
    opacity: 1;
    visibility: visible;
}

/* Sidebar */
.full-search-results-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    width: 450px;
    height: 100%;
    background: white;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    z-index: 1050;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    display: flex;
    flex-direction: column;
}

.full-search-results-sidebar:not(.sidebar-collapsed) {
    transform: translateX(0);
}

/* Sidebar Header */
.sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
}

.sidebar-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 350px;
}

.sidebar-toggle-btn {
    background: none;
    border: none;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s ease;
}

.sidebar-toggle-btn:hover {
    background: #e5e7eb;
    color: #374151;
}

/* Search Results Tabs */
.search-results-tabs {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.search-results-tabs :deep(.el-tabs) {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.search-results-tabs :deep(.el-tabs__header) {
    margin: 0;
    background: white;
    border-bottom: 1px solid #e5e7eb;
}

.search-results-tabs :deep(.el-tabs__content) {
    flex: 1;
    overflow: hidden;
}

.search-results-tabs :deep(.el-tab-pane) {
    height: 100%;
    overflow: hidden;
}

/* Results Container */
.results-container {
    height: 100%;
    overflow-y: auto;
    padding: 16px 0;
}

/* Loading State */
.loading-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #6b7280;
}

.loading-results svg {
    font-size: 24px;
    margin-bottom: 12px;
}

/* Empty State */
.empty-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #6b7280;
}

.empty-results svg {
    font-size: 32px;
    margin-bottom: 12px;
    opacity: 0.5;
}

/* Results List */
.results-list {
    padding: 0 16px;
}

/* Result Item */
.result-item {
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;
}

.result-item:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
}

.result-item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.result-item-left {
    display: flex;
    align-items: flex-start;
    flex: 1;
    min-width: 0;
}

.result-item-icon {
    font-size: 16px;
    color: #6b7280;
    margin-top: 2px;
    margin-right: 12px;
    flex-shrink: 0;
}

.result-item-details {
    flex: 1;
    min-width: 0;
}

.result-item-title {
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.result-item-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;
}

.result-item-meta span {
    font-size: 12px;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
}

.charge-code {
    font-family: monospace;
    background: #e5e7eb;
}

.status {
    text-transform: capitalize;
}

.status.approved {
    background: #d1fae5;
    color: #065f46;
}

.status.pending {
    background: #fed7aa;
    color: #92400e;
}

.status.rejected {
    background: #fee2e2;
    color: #991b1b;
}

.result-item-description {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.result-item-arrow {
    color: #9ca3af;
    margin-left: 12px;
    flex-shrink: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
    .full-search-results-sidebar {
        width: 100%;
    }
    
    .sidebar-title {
        max-width: 250px;
    }
}

/* Scrollbar Styling */
.results-container::-webkit-scrollbar {
    width: 6px;
}

.results-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.results-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.results-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>
